import { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Check, Loader2, AlertCircle } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OrderSuccessPage = () => {
  const navigate = useNavigate();
  const { orderId } = useParams();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  const [status, setStatus] = useState('loading');

  useEffect(() => {
    const provider = searchParams.get('provider');
    const paypalToken = searchParams.get('token');

    if (provider === 'paypal' && paypalToken) {
      // Capture PayPal payment
      const capturePayPal = async () => {
        try {
          const token = localStorage.getItem('token');
          await axios.post(`${API}/paypal/capture-order`, {
            paypal_order_id: paypalToken,
            order_id: orderId,
            order_type: 'checkout',
          }, { headers: { Authorization: `Bearer ${token}` } });
          setStatus('success');
        } catch (err) {
          console.error('PayPal capture error:', err);
          setStatus('error');
        }
      };
      capturePayPal();
    } else {
      setStatus('success');
    }
  }, [orderId, searchParams]);

  if (status === 'loading') {
    return (
      <div className="min-h-screen bg-amber-50 flex items-center justify-center" data-testid="order-success-loading">
        <Card className="max-w-md text-center p-8">
          <Loader2 className="w-12 h-12 animate-spin mx-auto mb-4 text-green-600" />
          <p className="text-lg">{t('checkout.processingPayment', 'Processing payment...')}</p>
        </Card>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen bg-amber-50 flex items-center justify-center" data-testid="order-success-error">
        <Card className="max-w-md text-center p-8">
          <AlertCircle className="w-12 h-12 mx-auto mb-4 text-red-500" />
          <h2 className="text-xl font-bold mb-2">{t('checkout.paymentFailed', 'Payment failed')}</h2>
          <p className="text-gray-600 mb-4">{t('checkout.paymentFailedMsg', 'Please try again or contact support')}</p>
          <Button onClick={() => navigate('/orders')} className="w-full">
            {t('orderSuccess.viewOrders', 'View Orders')}
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-amber-50 flex items-center justify-center" data-testid="order-success-page">
      <Card className="max-w-md text-center p-8">
        <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <Check className="w-10 h-10 text-green-600" />
        </div>
        <h2 className="text-2xl font-bold mb-2">{t('orderSuccess.title')}</h2>
        <p className="text-amber-600 mb-6">{t('orderSuccess.message')}</p>
        <div className="space-y-3">
          <Button onClick={() => navigate('/orders')} className="w-full bg-green-600 hover:bg-green-700">
            {t('orderSuccess.viewOrders')}
          </Button>
          <Button variant="outline" onClick={() => navigate('/')} className="w-full">
            {t('orders.continueShopping')}
          </Button>
        </div>
      </Card>
    </div>
  );
};

export default OrderSuccessPage;
